import React, { useCallback } from 'react';

import { useMessage } from '@tmapy/intl';
import { CustomSelect, FormItem } from '@tmapy/style-guide';

import { commonMessages } from '../../commonMessages';
import { useSelect } from '../../hooks';
import { ordersList } from '../../openapi/operations/ordersList';
import type { OrderListResponse } from '../../openapi/types/OrderList';
import { ORDER_STATE } from '../../orders/constants';

import { getDataOrderName } from '../utils';
import type { DataOrderValue } from '../utils';

import { serviceOrderFormMessages, serviceOrderMessages } from './serviceOrderMessages';

/** Pracovnice hledá konkrétní objednávku číslem nebo jménem, stačí první stránka výsledků. */
const DATA_ORDERS_PAGE_SIZE = 20;

const isOrderId = (term: string) => /^\d+$/.test(term);

type ServiceOrderDataOrderSelectProps = {
  name: string;
  value: DataOrderValue | null;
  errorMessage: string | null;
  isDisabled: boolean;
  onChange(value: DataOrderValue | null): void;
  /** Přepínač vazby, ať zůstane pod polem na stejném místě jako u ručně zadané plochy. */
  children?: React.ReactNode;
};

export const ServiceOrderDataOrderSelect: React.FC<ServiceOrderDataOrderSelectProps> = ({
  name,
  value,
  errorMessage,
  isDisabled,
  onChange,
  children,
}) => {
  const formatMessage = useMessage();

  const prepareItems = useCallback(async (search: string) => {
    const term = search.trim();
    const response = await ordersList({
      query: {
        state: ORDER_STATE.PUBLISHED,
        // Backend naváže i objednávku bez poplatku, přepážka ale spojuje jen ty s ním.
        handling_fee: true,
        page_size: DATA_ORDERS_PAGE_SIZE,
        ...(isOrderId(term) ? { id: Number(term) } : term ? { search: term } : {}),
      },
    });
    if (!response.ok) return null;
    return response.data?.results;
  }, []);

  const renderValue = useCallback(
    (order: DataOrderValue | null) => {
      if (!order) return '';
      const name = getDataOrderName(order);
      return name
        ? formatMessage(serviceOrderMessages.dataOrderNumberWithName, { id: order.id, name })
        : formatMessage(serviceOrderMessages.dataOrderNumber, { id: order.id });
    },
    [formatMessage],
  );

  const select = useSelect<DataOrderValue | null, OrderListResponse>({
    name,
    label: formatMessage(serviceOrderMessages.dataOrder),
    initialValue: value,
    onChange,
    prepareItems,
    renderItem: renderValue,
    renderValue,
  });

  return (
    <FormItem
      {...select}
      isRequired
      errorMessage={errorMessage}
      help={formatMessage(serviceOrderFormMessages.dataOrderHint)}
      helpLabel={formatMessage(commonMessages.formItemHelpLabel)}
    >
      <CustomSelect
        {...select}
        inputPlaceholder={formatMessage(serviceOrderFormMessages.dataOrderPlaceholder)}
        isDisabled={isDisabled}
        isInvalid={!!errorMessage}
      />
      {children}
    </FormItem>
  );
};
